import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Zap, ChevronRight, CheckCircle, X } from "lucide-react";
import { progressService } from "../../../../../services/progressService.js";
import { LiaSignalSolid } from "react-icons/lia";

const hasPositiveEnergy = (value) => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) && num > 0;
};

const buildItemsFromTasks = (tasks = []) => {
  const items = [];
  tasks.forEach((t) => {
    const hasSubtasks = Array.isArray(t.subtasks) && t.subtasks.length > 0;
    if (!hasSubtasks && hasPositiveEnergy(t.energy)) {
      items.push({
        id: `t-${String(t.id)}`,
        type: "task",
        taskId: t.id,
        name: t.name,
        energy: t.energy ?? null,
        category: t.category ?? null,
        delegationQuadrant: t.delegationQuadrant ?? null,
        delegateTo: t.delegateTo ?? t.assignedTo ?? "",
        delegationConstraint: t.delegationConstraint ?? t.constraint ?? "",
        delegationOrder: Number.isFinite(t.delegationOrder) ? t.delegationOrder : null,
      });
    }
    (t.subtasks || []).forEach((s) => {
      if (!hasPositiveEnergy(s.energy)) return;
      items.push({
        id: `s-${String(s.id)}`,
        type: "subtask",
        subtaskId: s.id,
        taskId: t.id,
        name: s.name,
        energy: s.energy ?? null,
        category: s.category ?? null,
        delegationQuadrant: s.delegationQuadrant ?? null,
        delegateTo: s.delegateTo ?? s.assignedTo ?? "",
        delegationConstraint: s.delegationConstraint ?? s.constraint ?? "",
        delegationOrder: Number.isFinite(s.delegationOrder) ? s.delegationOrder : null,
      });
    });
  });
  return items;
};

const buildTasksFromItems = (originalTasks = [], items = []) => {
  const lookup = {};
  items.forEach((it) => {
    lookup[it.id] = {
      category: it.category ?? null,
      delegationQuadrant: it.delegationQuadrant ?? null,
      delegationOrder: Number.isFinite(it.delegationOrder) ? it.delegationOrder : null,
    };
  });

  if (!originalTasks || originalTasks.length === 0) {
    const map = {};
    items.forEach((it) => {
      if (it.type === "task") {
        map[it.taskId] = {
          id: it.taskId,
          name: it.name,
          energy: it.energy,
          category: lookup[it.id]?.category ?? null,
          delegationQuadrant: lookup[it.id]?.delegationQuadrant ?? null,
          delegateTo: it.delegateTo ?? it.assignedTo ?? "",
          delegationConstraint: it.delegationConstraint ?? it.constraint ?? "",
          delegationOrder: lookup[it.id]?.delegationOrder ?? null,
          subtasks: [],
        };
      }
    });
    items.forEach((it) => {
      if (it.type === "subtask") {
        map[it.taskId] = map[it.taskId] || {
          id: it.taskId,
          name: `Task ${it.taskId}`,
          energy: null,
          category: null,
          delegationQuadrant: null,
          delegateTo: "",
          delegationConstraint: "",
          subtasks: [],
        };
        map[it.taskId].subtasks.push({
          id: it.subtaskId,
          name: it.name,
          energy: it.energy,
          category: lookup[it.id]?.category ?? null,
          delegationQuadrant: lookup[it.id]?.delegationQuadrant ?? null,
          delegateTo: it.delegateTo ?? it.assignedTo ?? "",
          delegationConstraint: it.delegationConstraint ?? it.constraint ?? "",
          delegationOrder: lookup[it.id]?.delegationOrder ?? null,
        });
      }
    });
    return Object.values(map);
  }

  return originalTasks.map((t) => {
    const taskItemId = `t-${String(t.id)}`;
    const taskData = lookup[taskItemId] ?? {};
    const updatedSubs = (t.subtasks || []).map((s) => {
      const subItemId = `s-${String(s.id)}`;
      const subData = lookup[subItemId] ?? {};
      return {
        ...s,
        category: subData.category ?? s.category ?? null,
        delegationQuadrant: subData.delegationQuadrant ?? s.delegationQuadrant ?? null,
        delegateTo: s.delegateTo ?? s.assignedTo ?? "",
        delegationConstraint: s.delegationConstraint ?? s.constraint ?? "",
        delegationOrder: subData.delegationOrder ?? s.delegationOrder ?? null,
      };
    });
    return {
      ...t,
      category: taskData.category ?? t.category ?? null,
      delegationQuadrant: taskData.delegationQuadrant ?? t.delegationQuadrant ?? null,
      delegateTo: t.delegateTo ?? t.assignedTo ?? "",
      delegationConstraint: t.delegationConstraint ?? t.constraint ?? "",
      delegationOrder: taskData.delegationOrder ?? t.delegationOrder ?? null,
      subtasks: updatedSubs,
    };
  });
};

const QUADRANT_IDS = {
  UNASSIGNED: "unassigned",
  DELEGATE_MONITOR: "delegate_monitor",
  FOCUS_YOURSELF: "focus_yourself",
  DELEGATE_FORGET: "delegate_forget",
  COACH_TEAM: "coach_team",
};

const quadrantLabel = {
  [QUADRANT_IDS.UNASSIGNED]: "LIST OF TASKS",
  [QUADRANT_IDS.DELEGATE_MONITOR]: "Delegate to most suitable person",
  [QUADRANT_IDS.FOCUS_YOURSELF]: "Focus deeply yourself",
  [QUADRANT_IDS.DELEGATE_FORGET]: "Delegate and forget",
  [QUADRANT_IDS.COACH_TEAM]: "Set up the essential foundation",
};

const quadrantSubtext = {
  [QUADRANT_IDS.DELEGATE_MONITOR]: "And monitor active & closely",
  [QUADRANT_IDS.FOCUS_YOURSELF]: "And share learning with your team after it's done",
  [QUADRANT_IDS.DELEGATE_FORGET]: "But clarify that you are always available for help",
  [QUADRANT_IDS.COACH_TEAM]: "And closely coach a team member to do it",
};

const normalizeDelegationOrder = (items = []) => {
  const ordered = items.map((item, idx) => ({
    ...item,
    delegationOrder: Number.isFinite(item.delegationOrder) ? item.delegationOrder : idx + 1,
  }));
  return ordered.sort((a, b) => (a.delegationOrder || 0) - (b.delegationOrder || 0));
};

const buildDelegationColumnsFromItems = (items = []) => {
  const cols = {
    [QUADRANT_IDS.UNASSIGNED]: {
      id: QUADRANT_IDS.UNASSIGNED,
      title: quadrantLabel[QUADRANT_IDS.UNASSIGNED],
      items: [],
    },
    [QUADRANT_IDS.DELEGATE_MONITOR]: {
      id: QUADRANT_IDS.DELEGATE_MONITOR,
      title: quadrantLabel[QUADRANT_IDS.DELEGATE_MONITOR],
      items: [],
    },
    [QUADRANT_IDS.FOCUS_YOURSELF]: {
      id: QUADRANT_IDS.FOCUS_YOURSELF,
      title: quadrantLabel[QUADRANT_IDS.FOCUS_YOURSELF],
      items: [],
    },
    [QUADRANT_IDS.DELEGATE_FORGET]: {
      id: QUADRANT_IDS.DELEGATE_FORGET,
      title: quadrantLabel[QUADRANT_IDS.DELEGATE_FORGET],
      items: [],
    },
    [QUADRANT_IDS.COACH_TEAM]: {
      id: QUADRANT_IDS.COACH_TEAM,
      title: quadrantLabel[QUADRANT_IDS.COACH_TEAM],
      items: [],
    },
  };

  items.forEach((it) => {
    const q = it.delegationQuadrant;
    if (!q) {
      cols[QUADRANT_IDS.UNASSIGNED].items.push(it);
    } else if (cols[q]) {
      cols[q].items.push(it);
    } else {
      cols[QUADRANT_IDS.UNASSIGNED].items.push(it);
    }
  });

  Object.keys(cols).forEach((key) => {
    cols[key].items = normalizeDelegationOrder(cols[key].items);
  });

  return cols;
};

// ✅ Toast Notification Component
const Toast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000); // Auto-dismiss after 5 seconds

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-6 right-6 z-50 animate-slideIn">
      <div className="bg-white rounded-lg shadow-lg border border-[#2E8B87] px-6 py-4 flex items-center gap-3 min-w-[300px]">
        <CheckCircle className="w-5 h-5 text-[#2E8B87] flex-shrink-0" />
        <p className="text-sm font-gotham font-medium text-[#2E8B87] flex-1">
          {message}
        </p>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const DelegationPlacement = ({ userId, subsectionId, onPreviousStep, onNextStep }) => {
  const service = progressService;

  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState({
    [QUADRANT_IDS.UNASSIGNED]: {
      id: QUADRANT_IDS.UNASSIGNED,
      title: quadrantLabel[QUADRANT_IDS.UNASSIGNED],
      items: [],
    },
    [QUADRANT_IDS.DELEGATE_MONITOR]: {
      id: QUADRANT_IDS.DELEGATE_MONITOR,
      title: quadrantLabel[QUADRANT_IDS.DELEGATE_MONITOR],
      items: [],
    },
    [QUADRANT_IDS.FOCUS_YOURSELF]: {
      id: QUADRANT_IDS.FOCUS_YOURSELF,
      title: quadrantLabel[QUADRANT_IDS.FOCUS_YOURSELF],
      items: [],
    },
    [QUADRANT_IDS.DELEGATE_FORGET]: {
      id: QUADRANT_IDS.DELEGATE_FORGET,
      title: quadrantLabel[QUADRANT_IDS.DELEGATE_FORGET],
      items: [],
    },
    [QUADRANT_IDS.COACH_TEAM]: {
      id: QUADRANT_IDS.COACH_TEAM,
      title: quadrantLabel[QUADRANT_IDS.COACH_TEAM],
      items: [],
    },
  });
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false); // ✅ Toast state

  const SHARED_MIN_HEIGHT_CLASS = "min-h-[560px]";

  const distributeItemsToColumns = useCallback((items) => {
    setColumns(buildDelegationColumnsFromItems(items));
  }, []);

  const areAllTasksPlaced = useCallback(() => {
    return (columns[QUADRANT_IDS.UNASSIGNED]?.items?.length ?? 0) === 0;
  }, [columns]);

  const loadedRef = useRef({ userId: null, subsectionId: null, done: false });
  const previousAllPlacedRef = useRef(false); // ✅ Track previous state

  useEffect(() => {
    async function fetchData() {
      if (!userId || !subsectionId) return;

      if (
        loadedRef.current.done &&
        loadedRef.current.userId === userId &&
        loadedRef.current.subsectionId === subsectionId
      ) {
        return;
      }

      try {
        setLoading(true);

        const saved = await service.getUserProgress(userId, subsectionId);
        const savedTasks =
          saved?.data?.tasks ??
          saved?.tasks ??
          saved?.data?.progress?.tasks;

        let sourceTasks = savedTasks;
        if (!Array.isArray(sourceTasks) || sourceTasks.length === 0) {
          const userData = await service.getUserDataForSection(
            userId,
            "course_career_progression",
            "delegation",
            "delegation_widget"
          );
          sourceTasks = userData?.tasks || [];
        }

        const latestTasks = (sourceTasks || []).map((t, idx) => ({
          id: t.id ?? idx + 1,
          name: t.name ?? `Task ${idx + 1}`,
          energy: typeof t.energy === "number" ? t.energy : t.energy ? Number(t.energy) : null,
          category: t.category ?? null,
          delegationQuadrant: t.delegationQuadrant ?? null,
          delegateTo: t.delegateTo ?? t.assignedTo ?? "",
          delegationConstraint: t.delegationConstraint ?? t.constraint ?? "",
          delegationOrder: Number.isFinite(t.delegationOrder) ? t.delegationOrder : null,
          subtasks: (t.subtasks || []).map((s, sidx) => ({
            id: s.id ?? `${Date.now()}-${sidx}`,
            name: s.name ?? `Subtask ${sidx + 1}`,
            energy: typeof s.energy === "number" ? s.energy : s.energy ? Number(s.energy) : null,
            category: s.category ?? null,
            delegationQuadrant: s.delegationQuadrant ?? null,
            delegateTo: s.delegateTo ?? s.assignedTo ?? "",
            delegationConstraint: s.delegationConstraint ?? s.constraint ?? "",
            delegationOrder: Number.isFinite(s.delegationOrder) ? s.delegationOrder : null,
          })),
        }));

        const syncTasks = (savedList, latestList) => {
          if (!savedList || savedList.length === 0) {
            return latestList;
          }

          const savedMap = new Map(savedList.map(t => [t.id, t]));

          return latestList.map(latestTask => {
            const savedTask = savedMap.get(latestTask.id);
            if (!savedTask) {
              return latestTask;
            }

            return {
              ...latestTask,
              category: savedTask.category ?? latestTask.category,
              delegationQuadrant: savedTask.delegationQuadrant ?? latestTask.delegationQuadrant,
              delegateTo: savedTask.delegateTo ?? savedTask.assignedTo ?? latestTask.delegateTo ?? latestTask.assignedTo,
              delegationConstraint: savedTask.delegationConstraint ?? savedTask.constraint ?? latestTask.delegationConstraint ?? latestTask.constraint,
              subtasks: latestTask.subtasks.map(latestSub => {
                const savedSub = savedTask.subtasks?.find(s => s.id === latestSub.id);
                if (!savedSub) return latestSub;

                return {
                  ...latestSub,
                  category: savedSub.category ?? latestSub.category,
                  delegationQuadrant: savedSub.delegationQuadrant ?? latestSub.delegationQuadrant,
                  delegateTo: savedSub.delegateTo ?? savedSub.assignedTo ?? latestSub.delegateTo ?? latestSub.assignedTo,
                  delegationConstraint: savedSub.delegationConstraint ?? savedSub.constraint ?? latestSub.delegationConstraint ?? latestSub.constraint,
                };
              }),
            };
          });
        };

        const syncedTasks = syncTasks(savedTasks, latestTasks);
        const baseItems = buildItemsFromTasks(syncedTasks);
        const cols = buildDelegationColumnsFromItems(baseItems);
        const allItems = Object.values(cols).flatMap((col) => col.items || []);
        const tasksWithQuadrants = buildTasksFromItems(syncedTasks, allItems);

        setTasks(tasksWithQuadrants);
        setColumns(cols);
        loadedRef.current = { userId, subsectionId, done: true };
      } catch (err) {
        console.error("Failed to load placement data:", err);
        setTasks([]);
        distributeItemsToColumns([]);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [userId, subsectionId, service, distributeItemsToColumns]);

  // ✅ Show toast when all tasks are placed
  useEffect(() => {
    const allPlaced = areAllTasksPlaced();
    
    // Only show toast if state changed from false to true
    if (allPlaced && !previousAllPlacedRef.current) {
      setShowToast(true);
    }
    
    previousAllPlacedRef.current = allPlaced;
  }, [columns, areAllTasksPlaced]);

  const savePlacement = async (cols) => {
    try {
      const normalizedCols = Object.keys(cols).reduce((acc, key) => {
        const col = cols[key];
        const normalizedItems = (col.items || []).map((item, idx) => ({
          ...item,
          delegationQuadrant: key === QUADRANT_IDS.UNASSIGNED ? null : key,
          delegationOrder: idx + 1,
        }));
        acc[key] = { ...col, items: normalizedItems };
        return acc;
      }, {});

      const allItems = Object.values(normalizedCols).flatMap((c) => c.items);
      const payloadTasks = buildTasksFromItems(tasks, allItems);

      await service.saveProgress(
        userId,
        subsectionId,
        { tasks: payloadTasks },
        "in_progress"
      );

      setColumns(normalizedCols);
      setTasks(payloadTasks);
    } catch (err) {
      console.error("DelegationPlacement: save failed:", err);
      // try one retry
      try {
        console.log("DelegationPlacement: retrying save once...");
        const allItems = Object.values(cols).flatMap((c) => c.items);
        const payloadTasks = buildTasksFromItems(tasks, allItems);
        await service.saveProgress(userId, subsectionId, { tasks: payloadTasks }, "in_progress");
        console.log("DelegationPlacement: retry successful");
      } catch (err2) {
        console.error("DelegationPlacement: retry failed:", err2);
      }
    }
  };

  // ----- REPLACE onDragEnd WITH THIS -----
  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const src = source.droppableId;
    const dest = destination.droppableId;

    if (!columns[src] || !columns[dest]) {
      console.warn("DelegationPlacement: invalid column on drag", src, dest);
      return;
    }

    // Same column - reorder
    if (src === dest) {
      const col = columns[src];
      const newItems = Array.from(col.items);
      const [moved] = newItems.splice(source.index, 1);
      newItems.splice(destination.index, 0, moved);
      
      const newCols = { ...columns, [src]: { ...col, items: newItems } };
      setColumns(newCols);
      savePlacement(newCols);
      return;
    }

    // Move across columns
    const sourceCol = columns[src];
    const destCol = columns[dest];
    const sourceItems = Array.from(sourceCol.items);
    const destItems = Array.from(destCol.items);
    const [movedItem] = sourceItems.splice(source.index, 1);

    const newQuadrant = destCol.id === QUADRANT_IDS.UNASSIGNED ? null : destCol.id;
    const updatedMoved = { ...movedItem, delegationQuadrant: newQuadrant };

    destItems.splice(destination.index, 0, updatedMoved);

    const newCols = {
      ...columns,
      [src]: { ...sourceCol, items: sourceItems },
      [dest]: { ...destCol, items: destItems },
    };

    setColumns(newCols);
    savePlacement(newCols);
  };

  // const handleSave = async () => {
  //   if (!service || typeof service.saveProgress !== "function") {
  //     console.warn("progressService.saveProgress not available.");
  //     return;
  //   }
  //   setIsSaving(true);
  //   try {
  //     const normalizedCols = Object.keys(columns).reduce((acc, key) => {
  //       const col = columns[key];
  //       const normalizedItems = (col.items || []).map((item, idx) => ({
  //         ...item,
  //         delegationQuadrant: key === QUADRANT_IDS.UNASSIGNED ? null : key,
  //         delegationOrder: idx + 1,
  //       }));
  //       acc[key] = { ...col, items: normalizedItems };
  //       return acc;
  //     }, {});
  //     const allItems = Object.values(normalizedCols).flatMap((c) => c.items);
  //     const payloadTasks = buildTasksFromItems(tasks, allItems);
  //     await service.saveProgress(userId, subsectionId, { tasks: payloadTasks }, "in_progress");
  //     setColumns(normalizedCols);
  //     setTasks(payloadTasks);
  //     alert("Progress saved successfully!");
  //   } catch (err) {
  //     console.error("Save failed:", err);
  //     alert("Save failed. Check console.");
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleFinish = async () => {
    if (!areAllTasksPlaced()) {
      alert("Please place all tasks in quadrants before proceeding.");
      return;
    }

    if (!service || typeof service.saveProgress !== "function") {
      console.warn("progressService.saveProgress not available.");
      return;
    }
    setIsSaving(true);
    try {
      const normalizedCols = Object.keys(columns).reduce((acc, key) => {
        const col = columns[key];
        const normalizedItems = (col.items || []).map((item, idx) => ({
          ...item,
          delegationQuadrant: key === QUADRANT_IDS.UNASSIGNED ? null : key,
          delegationOrder: idx + 1,
        }));
        acc[key] = { ...col, items: normalizedItems };
        return acc;
      }, {});
      const allItems = Object.values(normalizedCols).flatMap((c) => c.items);
      const payloadTasks = buildTasksFromItems(tasks, allItems);
      await service.saveProgress(userId, subsectionId, { tasks: payloadTasks }, "completed");
      setColumns(normalizedCols);
      setTasks(payloadTasks);
      onNextStep?.();
    } catch (err) {
      console.error("Finish failed:", err);
      alert("Finish failed. Check console.");
    } finally {
      setIsSaving(false);
    }
  };

  const TaskPill = ({ item, provided, snapshot }) => {
    const getCategoryBadge = () => {
      if (!item.category) return null;

      const badgeConfig = {
        leverage: { text: "L", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
        neutral: { text: "N", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
        overhead: { text: "O", bg: "bg-[#DFF3F1]", border: "border-[#54B0AF]" },
      };

      const config = badgeConfig[item.category];
      if (!config) return null;

      return (
        <span
          className={`${config.bg} ${config.border} border rounded-full px-3 py-1 text-[11px] 2xl:text-[12px] font-gotham font-medium text-[#000000] flex items-center gap-1`}
        >
          <LiaSignalSolid className="w-3 h-3 text-[#1B7977]" />
          {config.text}
        </span>
      );
    };

    const child = (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className="flex items-center justify-between gap-3 px-4 py-3 rounded-[9.82px] bg-white border border-[#E6E6E6] shadow-sm mb-3"
        style={{
          ...provided.draggableProps.style,
        }}
      >
        <div className="flex items-center gap-3 flex-1">
          <div className="flex gap-[2px]">
            <div className="flex flex-col gap-[2px]">
              <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
              <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
              <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
            </div>
            <div className="flex flex-col gap-[2px]">
              <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
              <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
              <span className="w-1 h-1 rounded-full bg-[#A3A3A3]" />
            </div>
          </div>
          <div className="text-[15px] 2xl:text-[15.71px] font-gotham mt-0.5 2xl:mt-0 font-medium text-[#000000]">
            {item.name}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="bg-[#F4F4F4] border border-[#E2F2F1] rounded-full px-3 py-2 flex items-center gap-2">
            <span className="text-[11px] 2xl:text-[12px] text-[#000000] font-medium font-gotham">
              {item.energy ?? "-"}%
            </span>
            <Zap className="w-4 h-4 text-[#54B0AF]" stroke="none" fill="#54B0AF" />
          </div>
          {getCategoryBadge()}
        </div>
      </div>
    );

    // Use portal when dragging to escape transformed parent
    if (snapshot?.isDragging) {
      return ReactDOM.createPortal(child, document.body);
    }

    return child;
  };

  const QuadrantCard = ({ col, position }) => {
    const bgColors = {
      top_left: "bg-[#DBF3F3]",
      top_right: "bg-[#B0DFDE]",
      bottom_left: "bg-[#F5FFFE]",
      bottom_right: "bg-[#DBF3F3]",
    };

    const borderColors = {
      top_left: "border-[#54B0AF]",
      top_right: "border-[#54B0AF]",
      bottom_left: "border-[#54B0AF]",
      bottom_right: "border-[#54B0AF]",
    };

    return (
      <div
        className={`rounded-[14px] ${bgColors[position]} ${borderColors[position]} border p-6 min-h-[310px] 2xl:min-h-[350px] flex flex-col`}
      >
        <div className="text-center text-[16.46px] font-gotham font-medium text-[#000000] leading-6 mb-2">
          {col.title}
        </div>
        <div className="text-center text-[12px] font-gotham font-normal text-[#323232] mb-4">
          {quadrantSubtext[col.id]}
        </div>

        <Droppable droppableId={col.id}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`flex-1 rounded-lg ${snapshot.isDraggingOver ? "bg-[#F0F9F8]" : ""} overflow-auto`}
            >
              {col.items.map((it, idx) => (
                <Draggable key={it.id} draggableId={it.id} index={idx}>
                  {(prov, snap) => <TaskPill item={it} provided={prov} snapshot={snap} />}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[300px] text-gray-500">
        Loading placement data...
      </div>
    );
  }

  // Ensure all quadrants exist before rendering
  const allQuadrantsExist = 
    columns[QUADRANT_IDS.UNASSIGNED] &&
    columns[QUADRANT_IDS.DELEGATE_MONITOR] &&
    columns[QUADRANT_IDS.FOCUS_YOURSELF] &&
    columns[QUADRANT_IDS.DELEGATE_FORGET] &&
    columns[QUADRANT_IDS.COACH_TEAM];

  if (!allQuadrantsExist) {
    console.warn("⚠️ Columns not properly initialized");
    return (
      <div className="flex items-center justify-center h-[300px] text-gray-500">
        Initializing quadrants...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-3">
      {/* ✅ Toast Notification */}
      {showToast && (
        <Toast
          message="✓ All tasks placed! Ready to proceed."
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="max-w-[1400px] mx-auto">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-stretch">
            <div className="lg:col-span-1">
              <div className="rounded-[14px] border border-[#CCCCCC] max-h-[740px] shadow-sm h-full flex flex-col bg-white">
                <div className="text-[15px] 2xl:text-[16px] font-medium text-[#6E6E6E] font-gotham px-4 pt-4 uppercase tracking-wide text-center border-b border-[#E6E6E6]">
                  {quadrantLabel[QUADRANT_IDS.UNASSIGNED]}
                </div>

                <Droppable droppableId={QUADRANT_IDS.UNASSIGNED}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`p-4 rounded-[14px] flex-1 overflow-y-auto max-h-[700px]${
                        snapshot.isDraggingOver ? "bg-[#F9F9F9]" : ""
                      }`}
                    >
                      {columns[QUADRANT_IDS.UNASSIGNED].items.map((it, idx) => (
                        <Draggable key={it.id} draggableId={it.id} index={idx}>
                          {(prov, snap) => <TaskPill item={it} provided={prov} snapshot={snap} />}
                        </Draggable>
                      ))}

                      {provided.placeholder}

                      {columns[QUADRANT_IDS.UNASSIGNED].items.length === 0 && (
                        <div className="text-[15.71px] text-[#6E6E6E] font-gotham font-medium text-center py-8">
                          All tasks placed!
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            </div>

            <div className="lg:col-span-2 relative flex flex-col">
              <div className="absolute left-[-30px] top-36 transform -rotate-90 origin-left">
                <span className="text-[13px] 2xl:text-[14px] font-gotham font-medium text-[#000000] tracking-wide">
                  Very High Leverage
                </span>
              </div>

              <div className={`${SHARED_MIN_HEIGHT_CLASS} grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8`}>
                <QuadrantCard
                  col={columns[QUADRANT_IDS.DELEGATE_MONITOR]}
                  position="top_left"
                />
                <QuadrantCard
                  col={columns[QUADRANT_IDS.FOCUS_YOURSELF]}
                  position="top_right"
                />
                <QuadrantCard
                  col={columns[QUADRANT_IDS.DELEGATE_FORGET]}
                  position="bottom_left"
                />
                <QuadrantCard col={columns[QUADRANT_IDS.COACH_TEAM]} position="bottom_right" />
              </div>

              <div className="absolute left-[-36px] bottom-14 md:bottom-16 lg:bottom-20 2xl:bottom-20 transform -rotate-90 origin-left">
                <span className="text-[13px] 2xl:text-[14px] font-gotham font-medium text-[#000000] tracking-wide">
                  Low Leverage
                </span>
              </div>

              <div className="flex justify-between mt-5 px-2">
                <span className="text-[13px] 2xl:text-[14px] font-gotham font-medium text-[#000000] tracking-wide">
                  Anyone can do
                </span>
                <span className="text-[13px] 2xl:text-[14px] font-gotham font-medium text-[#000000] tracking-wide">
                  Only you can do
                </span>
              </div>

              <div className="flex-1">
                <p className="text-center text-[#000000] font-medium font-gotham text-[16px] 2xl:text-[18px]">Who could do this work?</p>
              </div>
            </div>
          </div>
        </DragDropContext>

        <div className="w-full flex items-center justify-between gap-4 mt-8 lg:mt-10">
          <button
            onClick={onPreviousStep}
            className="flex items-center gap-3 px-6 py-3 font-gotham font-medium rounded-full bg-[#000000] text-white hover:bg-[#858585] transition-colors"
            aria-label="Previous Step"
          >
            <span className="bg-white rounded-full p-2">
              <ChevronRight className="w-4 h-4 rotate-180 text-black" />
            </span>
            Previous Step
          </button>

          <div className="flex items-center gap-4">
            <button
              onClick={handleFinish}
              disabled={isSaving || !areAllTasksPlaced()}
              className={`inline-flex items-center gap-4 px-6 py-3 rounded-full font-gotham font-medium text-lg transition-colors shadow ${
                areAllTasksPlaced() && !isSaving
                  ? "bg-black text-white hover:bg-neutral-800"
                  : "bg-[#BDBDBD] text-white cursor-not-allowed"
              }`}
              aria-label="Next Step"
            >
              Next Step
              <span className="bg-white rounded-full p-2">
                <ChevronRight className="w-4 h-4 text-black" />
              </span>
            </button>
          </div>
        </div>

        {/* ✅ REMOVED: Bottom center message - now using toast */}
        {!areAllTasksPlaced() && (
          <div className="mt-4 text-center text-sm text-[#666]">
            Please place all tasks in quadrants to proceed
          </div>
        )}
      </div>
      
      {/* ✅ Add animation styles to your global CSS or Tailwind config */}
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DelegationPlacement;